'use client'

import {
  fulfillmentLadderVolumeUnitLabels,
  fulfillmentShipmentTypeOrder,
  shipmentTypeLabels,
  type FulfillmentApi,
} from '@entities/fulfillment'
import { Button } from '@ui/base/button'
import { Checkbox } from '@ui/base/checkbox'
import { Field, FieldError, FieldLabel, FieldLegend, FieldSet } from '@ui/base/field'
import { InputWithUnit } from '@ui/base/input-with-unit'
import { Label } from '@ui/base/label'
import { Select, SelectContent, SelectItem, SelectPositioner, SelectTrigger, SelectValue } from '@ui/base/select'
import { PlusIcon, XIcon } from 'lucide-react'
import { useFormContext, useWatch, type UseFormRegisterReturn } from 'react-hook-form'

import { type ServicePriceLadderDraft } from '../lib/service-prices-form'
import { type FulfillmentFormValues } from '../model/types'
import { availableVolumeSteps } from '../model/volume-steps'

/** Порог объёма в списке выбора: значение уходит в форму, подпись видит владелец. */
type VolumeOption = { volume: number; label: string }

/**
 * Ступень лестницы: с какого объёма она действует и почём. Пустая цена законна — это «цена по
 * запросу». Крестика нет у единственной ступени: убрать её значило бы то же, что снять галочку.
 */
const LadderStep = ({
  volumeId,
  priceId,
  volumeFrom,
  volumeOptions,
  price,
  priceError,
  onChangeVolume,
  onRemove,
}: {
  volumeId: string
  priceId: string
  volumeFrom: number
  /** Пусто только у схемы «другое»: там лестницы не бывает, и выбирать порог не из чего. */
  volumeOptions: VolumeOption[]
  price: UseFormRegisterReturn
  priceError?: string
  onChangeVolume: (volumeFrom: number) => void
  onRemove?: () => void
}) => (
  <div className="flex flex-wrap items-end gap-2">
    {volumeOptions.length > 0 && (
      <Field className="w-44">
        <FieldLabel htmlFor={volumeId}>От объёма</FieldLabel>
        <Select value={String(volumeFrom)} onValueChange={(value) => onChangeVolume(Number(value))}>
          <SelectTrigger id={volumeId} className="w-full">
            <SelectValue />
          </SelectTrigger>
          <SelectPositioner>
            <SelectContent>
              {volumeOptions.map((option) => (
                <SelectItem key={option.volume} value={String(option.volume)}>
                  {option.label}
                </SelectItem>
              ))}
            </SelectContent>
          </SelectPositioner>
        </Select>
      </Field>
    )}
    <Field data-invalid={Boolean(priceError)} className="w-40">
      <FieldLabel htmlFor={priceId}>Цена</FieldLabel>
      <InputWithUnit
        id={priceId}
        unit="₽"
        inputMode="decimal"
        placeholder="по запросу"
        aria-invalid={Boolean(priceError)}
        aria-describedby={priceError ? `${priceId}-error` : undefined}
        {...price}
      />
      <FieldError id={`${priceId}-error`}>{priceError}</FieldError>
    </Field>
    {onRemove && (
      <Button
        type="button"
        variant="ghost"
        size="icon"
        aria-label={`Убрать ступень от ${volumeFrom}`}
        onClick={onRemove}
      >
        <XIcon />
      </Button>
    )}
  </div>
)

/** Услуга в одной схеме работы: оказываем или нет, а если оказываем — по какой лестнице цен. */
const ServiceRow = ({
  shipmentType,
  index,
  name,
}: {
  shipmentType: FulfillmentApi.ShipmentType
  index: number
  name: string
}) => {
  const { control, register, setValue, formState } = useFormContext<FulfillmentFormValues>()
  const path = `servicePrices.${shipmentType}.${index}` as const
  const service = useWatch({ control, name: path })
  const ladder = service?.ladder ?? []
  const volumeUnit = fulfillmentLadderVolumeUnitLabels[shipmentType]

  const usedVolumes = ladder.map((step) => step.volumeFrom)
  // Сетка порогов вместе с уже сохранёнными: порог, выбывший из настройки сервера, обязан остаться
  // допустимым там, где на нём уже стоит цена, иначе владелец увидит свою же цену недопустимой.
  const allVolumes = availableVolumeSteps(shipmentType, usedVolumes)

  /**
   * Пороги, которые можно выбрать этой ступени: занятые соседними ступенями исключены, свой
   * собственный оставлен. Две ступени на одном пороге дали бы лестницу, которую нельзя прочитать
   * однозначно, и сервер такую не примет.
   */
  const volumeOptionsFor = (stepIndex: number): VolumeOption[] =>
    allVolumes
      .filter((volume) => volume === usedVolumes[stepIndex] || !usedVolumes.includes(volume))
      .map((volume) => ({ volume, label: volumeUnit === null ? String(volume) : `${volume} ${volumeUnit}` }))

  // Следующая ступень заводится с порога выше всех занятых: лестница читается снизу вверх.
  const nextVolume =
    ladder.length === 0 ? allVolumes[0] : allVolumes.find((volume) => volume > Math.max(...usedVolumes))

  const checkboxId = `service-offered-${shipmentType}-${index}`

  const setLadder = (next: ServicePriceLadderDraft[]) => setValue(`${path}.ladder`, next, { shouldDirty: true })

  const offerService = (isOffered: boolean) => {
    setValue(`${path}.isOffered`, isOffered, { shouldDirty: true })
    // Первая ступень появляется вместе с галочкой: без неё вводить цену некуда.
    if (isOffered && ladder.length === 0 && nextVolume !== undefined) {
      setLadder([{ volumeFrom: nextVolume, price: '' }])
    }
  }

  const changeVolume = (stepIndex: number, volumeFrom: number) =>
    setLadder(ladder.map((step, itemIndex) => (itemIndex === stepIndex ? { ...step, volumeFrom } : step)))

  const removeStep = (stepIndex: number) => setLadder(ladder.filter((_, itemIndex) => itemIndex !== stepIndex))

  return (
    <div className="grid gap-2.5 border-b pb-4 last:border-b-0 last:pb-0">
      <Label htmlFor={checkboxId} className="cursor-pointer justify-start gap-2">
        <Checkbox id={checkboxId} checked={service?.isOffered ?? false} onCheckedChange={offerService} />
        <span>{name}</span>
      </Label>

      {service?.isOffered && (
        <div className="grid gap-2 sm:pl-7">
          {ladder.map((step, stepIndex) => (
            <LadderStep
              key={step.volumeFrom}
              volumeId={`${checkboxId}-volume-${stepIndex}`}
              priceId={`${checkboxId}-price-${stepIndex}`}
              volumeFrom={step.volumeFrom}
              volumeOptions={volumeOptionsFor(stepIndex)}
              price={register(`${path}.ladder.${stepIndex}.price`)}
              priceError={formState.errors.servicePrices?.[shipmentType]?.[index]?.ladder?.[stepIndex]?.price?.message}
              onChangeVolume={(volumeFrom) => changeVolume(stepIndex, volumeFrom)}
              onRemove={ladder.length > 1 ? () => removeStep(stepIndex) : undefined}
            />
          ))}

          {nextVolume !== undefined && (
            <Button
              type="button"
              variant="outline"
              size="sm"
              className="justify-self-start"
              onClick={() => setLadder([...ladder, { volumeFrom: nextVolume, price: '' }])}
            >
              <PlusIcon />
              Цена от большего объёма
            </Button>
          )}
        </div>
      )}
    </div>
  )
}

/**
 * Четвёртый шаг: что склад делает и почём. Цена задаётся отдельно по каждой схеме работы — одна и
 * та же услуга у FBS и FBO стоит по-разному. Пустое поле цены означает «цена по запросу»: это
 * законно и отличается от «услугу не оказываем», которое снимается галочкой.
 *
 * Услуги приходят справочником с сервера, и пустой справочник — законное состояние: поддержка
 * может ещё не завести услуги. Поэтому пустоту и отказ сервера здесь говорят разными словами:
 * в первом случае ждать нечего, во втором есть смысл повторить.
 */
export const PriceStep = ({
  services,
  isLoading,
  hasLoadError,
  onRetry,
}: {
  services: FulfillmentApi.ServiceOption[] | undefined
  isLoading: boolean
  hasLoadError: boolean
  onRetry: () => void
}) => {
  const { control } = useFormContext<FulfillmentFormValues>()
  const servicePrices = useWatch({ control, name: 'servicePrices' })
  const selectedShipmentTypes = useWatch({ control, name: 'shipmentTypes' })
  const shipmentTypes = fulfillmentShipmentTypeOrder.filter((shipmentType) =>
    selectedShipmentTypes?.includes(shipmentType)
  )

  if (isLoading) {
    return <p role="status">Загружаем справочник услуг…</p>
  }

  if (hasLoadError) {
    return (
      <div className="grid justify-items-start gap-3">
        <FieldError>Не удалось загрузить справочник услуг. Попробуйте ещё раз.</FieldError>
        <Button type="button" variant="outline" onClick={onRetry}>
          Повторить загрузку
        </Button>
      </div>
    )
  }

  if (!services?.length) {
    return (
      <FieldError>
        Справочник услуг пока пуст, заполнить прайс не получится. Сохраните карточку без цен и вернитесь к ней позже.
      </FieldError>
    )
  }

  if (shipmentTypes.length === 0) {
    return <FieldError>Сначала отметьте схемы работы на шаге отгрузки: цена задаётся по каждой отдельно.</FieldError>
  }

  const serviceNames = new Map(services.map((service) => [service.id, service.name]))

  return (
    <>
      {shipmentTypes.map((shipmentType) => (
        <FieldSet key={shipmentType}>
          <FieldLegend variant="section-divided">{shipmentTypeLabels[shipmentType]}</FieldLegend>
          <div className="grid gap-4">
            {(servicePrices?.[shipmentType] ?? []).map((service, index) => (
              <ServiceRow
                key={service.serviceId}
                shipmentType={shipmentType}
                index={index}
                name={serviceNames.get(service.serviceId) ?? 'Услуга'}
              />
            ))}
          </div>
        </FieldSet>
      ))}
    </>
  )
}
